import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  PencilIcon,
  ArchiveBoxIcon,
  TrashIcon,
  ArrowUturnUpIcon,
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { Contact, ContactType, formatAddress } from "@akfdev-com/shared";
import { contactsApi } from "../../services/api";
import { ContactModules } from "./ContactModules";
import { FinancialRecordsTable } from "./FinancialRecordsTable";
import { FinancialSummaryPanel } from "./FinancialSummaryPanel";
import { useContactFinancials } from "./useContactFinancials";
import type { RecordFilterModel } from "./types";

// Type badge colors per design-language (design-language.md, Contact Type Badges)
const typeColors: Record<ContactType, { bg: string; text: string }> = {
  resident: {
    bg: "#ede9fe",
    text: "#6d28d9",
  },
  supplier: {
    bg: "#fef3c7",
    text: "#92400e",
  },
  customer: {
    bg: "#dbeafe",
    text: "#1e40af",
  },
};

/**
 * Contact Details Page
 *
 * Displays a single contact with type-aware module layout:
 * - Calls useContactFinancials for financial data (getByContact)
 * - Uses ContactModules to determine visibility by contact type
 * - Renders FinancialRecordsTable and FinancialSummaryPanel into module slots
 * - Shows empty state when no records, error state on read failure
 *
 * Validates: Requirements 2.1, 2.8, 2.9, 5.1, 5.3, 5.4, 5.5, 7.1, 8.5
 */
const ContactDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contact, setContact] = useState<Contact | null>(null);
  const [contactLoading, setContactLoading] = useState(true);
  const [contactError, setContactError] = useState<string | null>(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Financial data from hook
  const {
    records: financialRecords,
    loading: financialLoading,
    error: financialError,
    empty: financialEmpty,
    refetch: refetchFinancials,
  } = useContactFinancials(id || "");

  // Record filter state
  const [filter, setFilter] = useState<RecordFilterModel>({
    type: "All",
    status: null,
  });

  // Load contact
  useEffect(() => {
    const fetchContact = async () => {
      if (!id) return;
      try {
        setContactLoading(true);
        const data = await contactsApi.get(id);
        setContact(data);
      } catch {
        setContactError("Failed to load contact.");
      } finally {
        setContactLoading(false);
      }
    };
    fetchContact();
  }, [id]);

  const handleArchiveToggle = async () => {
    if (!id || !contact) return;
    try {
      const updated = contact.archivedAt
        ? await contactsApi.unarchive(id)
        : await contactsApi.archive(id);
      setContact(updated);
    } catch {
      setContactError("Failed to update contact status.");
    } finally {
      setShowArchiveConfirm(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await contactsApi.delete(id);
      navigate("/dashboard/contacts");
    } catch {
      setContactError("Failed to delete contact.");
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  if (contactLoading) return <Spinner message="Loading contact..." />;
  if (contactError) return <ErrorAlert message={contactError} />;
  if (!contact) return <ErrorAlert message="Contact not found." />;

  const isArchived = !!contact.archivedAt;

  return (
    <div>
      <div className="mb-4">
        <Link
          to="/dashboard/contacts"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Contacts
        </Link>
      </div>

      <PageHeader
        title={
          <div className="flex items-center gap-3">
            <span>{contact.name}</span>
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold"
              style={{
                backgroundColor: typeColors[contact.type].bg,
                color: typeColors[contact.type].text,
              }}
            >
              {contact.type.charAt(0).toUpperCase() + contact.type.slice(1)}
            </span>
          </div>
        }
        description={isArchived ? "This contact is archived." : undefined}
        actions={
          <div className="flex items-center gap-2">
            <Link
              to={`/dashboard/contacts/${id}/edit`}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </Link>
            <button
              type="button"
              onClick={() => setShowArchiveConfirm(true)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 border text-sm font-medium rounded-md transition-colors ${
                isArchived
                  ? "border-green-300 text-green-700 bg-white hover:bg-green-50"
                  : "border-yellow-300 text-yellow-700 bg-white hover:bg-yellow-50"
              }`}
            >
              {isArchived ? (
                <>
                  <ArrowUturnUpIcon className="h-4 w-4" />
                  Unarchive
                </>
              ) : (
                <>
                  <ArchiveBoxIcon className="h-4 w-4" />
                  Archive
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              <TrashIcon className="h-4 w-4" />
              Delete
            </button>
          </div>
        }
      />

      {isArchived && (
        <div className="mt-4 rounded-md bg-yellow-50 border border-yellow-200 px-4 py-3 flex items-center gap-2">
          <ArchiveBoxIcon className="h-5 w-5 text-yellow-600 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            Archived on{" "}
            {new Date(contact.archivedAt!).toLocaleDateString("en-GB")}
          </p>
        </div>
      )}

      <div className="mt-6">
        <ContactModules type={contact.type}>
          {{
            contactInformation: (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">
                    Contact Information
                  </h2>
                </div>
                <dl className="divide-y divide-gray-100">
                  <div className="px-6 py-4 flex items-start gap-3">
                    <dt className="w-32 flex-shrink-0 text-sm font-medium text-gray-500">
                      Primary Contact
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {contact.name || "—"}
                    </dd>
                  </div>

                  <div className="px-6 py-4 flex items-start gap-3">
                    <dt className="w-32 flex-shrink-0 text-sm font-medium text-gray-500 flex items-center gap-1">
                      <EnvelopeIcon className="h-4 w-4" /> Email
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {contact.email ? (
                        <a
                          href={`mailto:${contact.email}`}
                          className="text-blue-600 hover:underline"
                        >
                          {contact.email}
                        </a>
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>

                  <div className="px-6 py-4 flex items-start gap-3">
                    <dt className="w-32 flex-shrink-0 text-sm font-medium text-gray-500 flex items-center gap-1">
                      <PhoneIcon className="h-4 w-4" /> Phone
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {contact.phone ? (
                        <a
                          href={`tel:${contact.phone}`}
                          className="text-blue-600 hover:underline"
                        >
                          {contact.phone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>

                  <div className="px-6 py-4 flex items-start gap-3">
                    <dt className="w-32 flex-shrink-0 text-sm font-medium text-gray-500 flex items-center gap-1">
                      <MapPinIcon className="h-4 w-4" /> Address
                    </dt>
                    <dd className="text-sm text-gray-900 whitespace-pre-wrap">
                      {contact.structuredAddress
                        ? formatAddress(contact.structuredAddress)
                        : contact.address && contact.address.trim().length > 0
                        ? contact.address
                        : "—"}
                    </dd>
                  </div>

                  {/* Bucket C #9 read-only status. Shown for every
                      contact so the treasurer can audit the flag and
                      toggle it from ContactEdit if wrong. */}
                  <div className="px-6 py-4 flex items-start gap-3">
                    <dt className="w-32 flex-shrink-0 text-sm font-medium text-gray-500">
                      Import behaviour
                    </dt>
                    <dd className="text-sm text-gray-900">
                      {contact.autoServiceChargeOnImport ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 text-xs font-medium">
                          Auto-classify future invoices as service charges
                        </span>
                      ) : (
                        <span className="text-gray-500">
                          Future invoices land as plain invoices.
                        </span>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Toggle from the Edit page. Auto-reclassification
                        requires the contact to be linked to a unit and the
                        building to have exactly one active service-charge plan.
                      </p>
                    </dd>
                  </div>
                </dl>
              </div>
            ),

            billingFinancialSummary: (
              <FinancialSummaryPanel records={financialRecords} period="FY" />
            ),

            invoices: (
              <FinancialRecordsTable
                records={financialRecords}
                filter={filter}
                onFilterChange={setFilter}
                onAfterDelete={refetchFinancials}
                contact={contact}
                loading={financialLoading}
                empty={financialEmpty}
              />
            ),

            bills: (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">
                    Bills
                  </h2>
                </div>
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-500">Coming soon</p>
                </div>
              </div>
            ),

            serviceCharges: (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">
                    Service Charges
                  </h2>
                </div>
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-500">Coming soon</p>
                </div>
              </div>
            ),

            defaultServiceCharges: (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">
                    Default Service Charges
                  </h2>
                </div>
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-500">Coming soon</p>
                </div>
              </div>
            ),

            notes: (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">
                    Notes
                  </h2>
                </div>
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-500">Coming soon</p>
                </div>
              </div>
            ),

            documents: (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">
                    Documents
                  </h2>
                </div>
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-500">Coming soon</p>
                </div>
              </div>
            ),

            history: (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">
                    History
                  </h2>
                </div>
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-500">Coming soon</p>
                </div>
              </div>
            ),

            legend: (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">
                    Module States
                  </h2>
                </div>
                <div className="px-6 py-6 space-y-4">
                  {/* ACTIVE state */}
                  <div className="flex items-start gap-3">
                    <div
                      className="mt-1 flex-shrink-0 h-3 w-3 rounded"
                      style={{ backgroundColor: "#dcfce7" }}
                      title="Active module indicator"
                    />
                    <div>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: "#166534" }}
                      >
                        Active
                      </p>
                      <p className="text-xs text-gray-600">
                        Visible and interactive
                      </p>
                    </div>
                  </div>

                  {/* GREYED state */}
                  <div className="flex items-start gap-3">
                    <div
                      className="mt-1 flex-shrink-0 h-3 w-3 rounded"
                      style={{ backgroundColor: "#fef3c7" }}
                      title="Greyed module indicator"
                    />
                    <div>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: "#b45309" }}
                      >
                        Greyed
                      </p>
                      <p className="text-xs text-gray-600">
                        Disabled and not interactive
                      </p>
                    </div>
                  </div>

                  {/* HIDDEN state */}
                  <div className="flex items-start gap-3">
                    <div
                      className="mt-1 flex-shrink-0 h-3 w-3 rounded"
                      style={{ backgroundColor: "#f3f4f6" }}
                      title="Hidden module indicator"
                    />
                    <div>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: "#9ca3af" }}
                      >
                        Hidden
                      </p>
                      <p className="text-xs text-gray-600">
                        Not shown for this contact type
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ),
          }}
        </ContactModules>
      </div>

      <ConfirmDialog
        isOpen={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        onConfirm={handleArchiveToggle}
        title={isArchived ? "Unarchive Contact" : "Archive Contact"}
        message={
          isArchived
            ? `Restore "${contact.name}" to the active contacts list?`
            : `Archive "${contact.name}"?`
        }
        confirmText={isArchived ? "Unarchive" : "Archive"}
        confirmButtonClass={
          isArchived
            ? "bg-green-600 hover:bg-green-700"
            : "bg-yellow-600 hover:bg-yellow-700"
        }
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Contact"
        message={`Delete "${contact.name}"? This cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
};

export default ContactDetails;
